import { useQuery } from "@tanstack/react-query";
import { Car, Gauge, MapPin, Phone, Repeat } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import { formatCurrency, type Vehicle } from "@/lib/types";

export function Cars() {
  const { data: vehicles = [], isLoading } = useQuery<Vehicle[]>({
    queryKey: ["public-vehicles"],
    queryFn: async () => {
      const res = await fetch("/api/public/vehicles");
      if (!res.ok) throw new Error("Failed to fetch vehicles");
      return res.json();
    },
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Fresh Modern Design */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/cars" className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-sm shadow-indigo-500/20">
                <Repeat className="size-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">General Auto</h1>
                <p className="text-[11px] text-slate-400">รถมือสองคุณภาพดี</p>
              </div>
            </Link>
            <a
              href="tel:+66812345678"
              className="flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-indigo-500/20 transition-colors hover:bg-indigo-600"
            >
              <Phone className="size-4" />
              <span className="hidden sm:inline">081-234-5678</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 text-center sm:px-6 sm:py-12 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">ค้นหารถในฝันของคุณ</h2>
          <p className="mt-3 text-slate-500">รถมือสองคุณภาพดี ราคาโปร่งใส รับประกันหลังการขาย</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700">
              <span className="size-2 rounded-full bg-indigo-500" />
              {vehicles.length} คันพร้อมขาย
            </span>
          </div>
        </div>
      </div>

      {/* Vehicle Grid */}
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="aspect-[4/3] bg-slate-100" />
                <div className="p-5">
                  <div className="h-6 w-3/4 rounded-lg bg-slate-100" />
                  <div className="mt-2 h-4 w-1/2 rounded-lg bg-slate-100" />
                  <div className="mt-4 h-8 w-1/3 rounded-lg bg-slate-100" />
                  <div className="mt-4 h-10 w-full rounded-xl bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex size-20 items-center justify-center rounded-2xl bg-slate-100">
                <Car className="size-10 text-slate-400" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-slate-900">ไม่มีรถพร้อมขายในขณะนี้</h3>
              <p className="mt-2 text-sm text-slate-500">กรุณาติดต่อเราเพื่อสอบถามรถที่กำลังจะมา</p>
              <a href="tel:+66812345678">
                <Button className="mt-6 rounded-xl bg-indigo-500 hover:bg-indigo-600">
                  <Phone className="mr-2 size-4" />
                  โทรสอบถาม
                </Button>
              </a>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        )}
      </main>

      {/* Contact Section */}
      <section className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-2xl font-bold text-slate-900">ติดต่อเรา</h3>
              <p className="mt-2 text-slate-500">
                สนใจรถคันไหน หรืออยากสอบถามข้อมูลเพิ่มเติม ติดต่อเราได้เลย
              </p>
              <div className="mt-6 space-y-4">
                <a
                  href="tel:+66812345678"
                  className="flex items-center gap-3 text-slate-900 transition-colors hover:text-indigo-600"
                >
                  <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-50">
                    <Phone className="size-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">โทรศัพท์</p>
                    <p className="font-medium">081-234-5678</p>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50">
                    <MapPin className="size-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">ที่อยู่</p>
                    <p className="font-medium text-slate-900">
                      123 ถนนพระราม 4
                      <br />
                      กรุงเทพฯ 10110
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-900">เวลาทำการ</h3>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between rounded-lg bg-slate-50 px-4 py-2.5">
                  <span className="text-slate-500">จันทร์ - เสาร์</span>
                  <span className="font-medium text-slate-900">9:00 - 19:00 น.</span>
                </div>
                <div className="flex justify-between rounded-lg bg-slate-50 px-4 py-2.5">
                  <span className="text-slate-500">อาทิตย์</span>
                  <span className="font-medium text-slate-900">10:00 - 17:00 น.</span>
                </div>
              </div>
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 p-5 text-center text-white shadow-lg shadow-indigo-500/20">
                <p className="text-sm text-indigo-100">นัดดูรถได้ทุกวัน</p>
                <p className="mt-1 text-xl font-bold">โทร 081-234-5678</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600">
              <Repeat className="size-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">General Auto</span>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            แหล่งรวมรถมือสองคุณภาพดี ราคาโดน ไว้วางใจได้
          </p>
          <p className="mt-4 text-xs text-slate-400">
            © {new Date().getFullYear()} General Auto Cars. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
  // Get the primary image or first image
  const imageUrl =
    vehicle.images?.find((img) => img.is_primary)?.image_url ||
    vehicle.images?.[0]?.image_url ||
    vehicle.image_url;

  return (
    <Link
      to={`/listing/${vehicle.id}`}
      className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:border-indigo-200 hover:shadow-lg"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Car className="size-16 text-slate-300" />
          </div>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-indigo-500 px-2.5 py-1 text-xs font-medium text-white">
          พร้อมขาย
        </span>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-slate-900">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
          {vehicle.mileage && (
            <span className="flex items-center gap-1">
              <Gauge className="size-3.5" />
              {vehicle.mileage.toLocaleString("th-TH")} กม.
            </span>
          )}
          {vehicle.color && <span>• {vehicle.color}</span>}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-2xl font-bold text-indigo-600">{formatCurrency(vehicle.price)}</p>
          <Button className="rounded-xl bg-slate-900 text-sm hover:bg-slate-800">
            ดูรายละเอียด
          </Button>
        </div>
      </div>
    </Link>
  );
}
